import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from '@chakra-ui/react';
import { useGlobalContext } from 'GlobalContext';
import { useState } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { changeManyContractPaymentMethod } from 'services/contract';
import { Contract } from 'shared/models/contract';
interface ChangeManyPaymentMethodModalProps {
  isOpen: boolean;
  onClose(): void;
  contracts: Contract[];
}

const ChangeManyPaymentMethodModal: React.FC<
  ChangeManyPaymentMethodModalProps
> = ({ isOpen, onClose: onCloseFromProps, contracts }) => {
  const { confirm } = useGlobalContext();

  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState<1 | 2>(1);

  const activeContracts = contracts.filter(
    (c) => c.status === 1 && !c.deleted_at,
  );

  const inactiveCount = contracts.length - activeContracts.length;

  const onClose = () => {
    onCloseFromProps();
    setLoading(false);
    setMethod(1);
  };

  const submit = () => {
    if (method === undefined || activeContracts.length === 0) {
      return toast.error('Nenhum contrato ativo encontrado para alteração.');
    }

    const contractIds = activeContracts.map((c) => c.id!);
    const methodLabel = method === 1 ? 'Cartão de Crédito' : 'Boleto Bancário';

    confirm(
      `Alterar método de pagamento de ${activeContracts.length} contrato(s) para ${methodLabel}`,
      'Deseja continuar?',
      async () => {
        setLoading(true);
        try {
          const response = await changeManyContractPaymentMethod(
            contractIds,
            method,
          );

          toast.success(
            `Método de pagamento alterado para ${response?.data?.successContracts?.length} contrato(s)`,
          );

          if (response?.data?.failed.length > 0) {
            toast.error(
              `Falha ao alterar método de pagamento para ${response?.data?.failed?.length} contrato(s)`,
            );
          }

          onClose();
        } catch (e) {
          const axiosError = e as AxiosError;
          toast.error('Falha ao alterar método de pagamento dos contratos');
          console.error(
            'Falha ao alterar método de pagamento dos contratos',
            axiosError?.response?.data
              ? JSON.stringify(axiosError?.response?.data, null, 2)
              : JSON.stringify(axiosError?.response, null, 2),
          );
        } finally {
          setLoading(false);
        }
      },
    );
  };

  return (
    <Modal
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton isDisabled={loading} />
        <ModalHeader>Alterar Método de Pagamento em Lote</ModalHeader>

        <ModalBody>
          <Card bg="yellow.100" mb="1rem">
            <CardBody>
              <HStack gap="1rem">
                <Icon as={MdInfoOutline} color="yellow.500" />
                <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                  Só é possível mudar o método de pagamento de um cliente com um
                  intervalo de 30 dias desde a última alteração.
                </Text>
              </HStack>
            </CardBody>
          </Card>

          <Card bg="blue.100" mb="1rem">
            <CardBody>
              <HStack gap="1rem">
                <Icon as={MdInfoOutline} color="blue.500" />
                <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                  Todos os contratos <strong>ativos</strong> terão o método de
                  pagamento alterado.
                </Text>
              </HStack>
            </CardBody>
          </Card>

          {inactiveCount > 0 && (
            <Card bg="yellow.100" mb="1rem">
              <CardBody>
                <HStack gap="1rem">
                  <Icon as={MdInfoOutline} color="yellow.500" />
                  <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                    Contratos inativos ou cancelados não serão afetados por esta
                    alteração.
                  </Text>
                </HStack>
              </CardBody>
            </Card>
          )}

          <Text fontSize="0.85rem" color="gray.500" mb="0.5rem">
            {activeContracts.length} contrato(s) serão alterados.
          </Text>

          <FormControl mt="0.5rem">
            <FormLabel fontSize="0.8rem" color="gray.500">
              Novo método de pagamento
            </FormLabel>
            <Select
              id="select-many-payment-method"
              value={String(method)}
              onChange={(e) => setMethod(Number(e.target.value) as 1 | 2)}
              isDisabled={loading}
            >
              <option value="1">Cartão de Crédito</option>
              <option value="2">Boleto Bancário</option>
            </Select>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            isLoading={loading}
            onClick={submit}
            isDisabled={loading || activeContracts.length === 0}
            colorScheme="yooga"
            w="100%"
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangeManyPaymentMethodModal;
